import React from 'react';
import RIEBase from 'components/core/editable/RIEBase';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import Flexbox from 'flexbox-react';
import ComboBoxHOC from 'components/core/ComboBoxHOC';

/**
 * Editable component for a combo box which allows you to pick from a list or
 * type in a custom value.  We replaced RIETags with this component.
 *
 * Select component comes from https://github.com/JedWatson/react-select
 */
export default class RIEComboBox extends RIEBase {
    static defaultProps = {
        creatable: false,
        multi: false,
        clearable: false
    };

    static propTypes = {
        options: PropTypes.array,
        clearable: PropTypes.bool, // Will the select box show an X to reset the value
        creatable: PropTypes.bool, // Will the select box allow users to enter new value(s)
        multi: PropTypes.bool, // can the user choose multiple values
        tagClassNames: PropTypes.object, // maps tag name to className for rendering in read-only mode
        tagDefaultClassName: PropTypes.string, // default class name to use if tag not found in tagClassNames
        isValidNewOption: PropTypes.func // function that will determine if the selected option is valid
    };

    getMinWidth = () => {
        let minWidth = '250px';
        if (this.props.creatable) {
            minWidth = '100%';
        }

        return minWidth;
    };

    getOptions = () => {
        // react-select adds new user tags to the options list - we don't want to do that, so we make a copy of the
        // original options list so it is not corrupted
        return Object.assign([], this.props.options);
    };

    /**
     * Value is normally an array of object, one for each value in the combo box.
     * Each object in the array looks like this:
     * {value: "kasacr2cfrspeccwrhicopol.a1", label: "kasacr2cfrspeccwrhicopol.a1"}
     * @param value
     */
    onChange = (value, actionMeta) => {
        const clearable = this.props.clearable;
        const multi = this.props.multi;
        if (value && value.value) {
            value = { value: value.value.trim(), label: value.label.trim()};
        }
        let newValue = value;

        // this can happen if the user clears out the field and there is no reset value
        if (!value || (value instanceof Array && value.length === 0) || !(value instanceof Object) || (value instanceof Object && value.value && value.value.length === 0)) {
            if (clearable) {
                // It is ok to set the value as null (i.e., clear the value)
                if (multi) {
                    newValue = [];
                } else {
                    newValue = { value: null, label: '' };
                }
            } else {
                // We should revert to the previous value
                newValue = this.props.value;
            }

        } else {
            const explode = text => {
                const splitValues = text.split(',');
                const values = [];
                splitValues.forEach(split => {
                    const trimmed = split.trim();
                    if (trimmed !== '') {
                        // Only add if this value isn't already in the list
                        if (!_.includes(values, trimmed)) {
                            values.push({ value: trimmed, label: trimmed });
                        }
                    }
                });
                return values;
            };

            // We need to parse each value to see if contains separator characters, and if so explode into more
            // values
            if (this.props.multi) { // only explode if we are in multi mode
                newValue = [];
                value.forEach(singleValue => {
                    newValue = newValue.concat(explode(singleValue.value));
                });

            }
        }
        this.setState({ newValue });
    };

    keyDown = (event) => {
        if (event.keyCode === 13) { // Enter
            // stop OK on confirm modal from being fired before finish editing runs
            event.stopPropagation();

            // if dropdown isn't open enter finishes editing
            if (!this.comboBoxHOCRef.current.comboBoxRef.current.state.menuIsOpen) {
                this.elementBlur(event, true);
            }
        } else if (event.keyCode === 27) { // Escape
            this.cancelEditing();
        }
    };

    renderReadComponent() {
        const values = this.getNewValue();
        if (Array.isArray(values)) {
            const idMap = {};
            values.forEach((item) => {
                //need to remove dots and be sure to start with a letter as this is used in
                //html's id's prop which fails if either of the above occur
                idMap[item.value] = `id-${_.replace(item.value, '.', '-')}`;
            });

            // calculate the number of cols
            // estimate of max width of column = 200px
            const numCols = values.length > 10 ? 3 : 1;
            const numRows = Math.ceil(values.length / numCols);
            const columns = [];
            let valueIndex = 0;

            for (let i = 0; i < numCols; i++) {
                const colTags = [];

                for (let j = 0; j < numRows; j++) {
                    if (valueIndex < values.length) {

                        const tag = values[valueIndex];
                        let className = '';
                        if (this.props.tagClassNames && this.props.tagClassNames[tag.value]) {
                            className = this.props.tagClassNames[tag.value];
                        } else if (this.props.tagDefaultClassName) {
                            className = this.props.tagDefaultClassName;
                        }

                        const style = { display: 'block' };
                        colTags.push(
                            <span
                                className={`tag ${className}`}
                                style={style}
                                key={tag.value}
                                id={`${idMap[tag.value]}`}
                            >
                                {tag.label}
                            </span>
                        );
                        valueIndex++;
                    }
                }

                const col = (
                    <Flexbox key={`col-${i}`} flexDirection="column" padding="0 15px 0 0">
                        {colTags}
                    </Flexbox>
                );
                columns.push(col);
            }
            const tags = (
                <Flexbox flexDirection="row">
                    {columns}
                </Flexbox>
            );
            // add onFocus back in order to go into edit mode via tab key then refactor RIE base so context menu will still work
            // onFocus={this.startEditing}
            return (
                <span
                    tabIndex="0"
                    className={`${this.makeClassString()} tags-read-only`}
                    onClick={this.startEditing}
                    {...this.props.defaultProps}
                >
                    {tags}
                </span>
            );
        } else {
            const value = values;
            const label = value ? value.label : '';

            // add onFocus back in order to go into edit mode via tab key then refactor RIE base so context menu will still work
            // onFocus={this.startEditing}
            return (
                <span
                    tabIndex="0"
                    className={this.makeClassString()}
                    onClick={this.startEditing}
                    {...this.props.defaultProps}
                >
                    {label}
                </span>
            );
        }
    }

    renderNormalComponent = () => {
        const value = this.getNewValue();

        return (
            <div
                className={`combo-wrapper edit-mode ${this.makeClassString()}`}
                {...this.props.editProps}
            >
                <ComboBoxHOC
                    creatable={this.props.creatable}
                    classNamePrefix="react-select"
                    value={value}
                    options={this.getOptions()}
                    autoFocus={false}
                    isMulti={this.props.multi}
                    isClearable={this.props.clearable}
                    {...this.getStartEditingProps()}
                />
            </div>
        );
    };

    renderEditingComponent = () => {
        const value = this.getNewValue();
        this.comboBoxHOCRef = React.createRef();

        // react-select adds new user tags to the options list - we don't want to do that, so we make a copy of the
        // original options list so it is not corrupted
        //const options = Object.assign([], this.props.options);

        // We forward some properties to react-select
        // See here for more description on the props https://github.com/JedWatson/react-select
        return (
            <div className={`combo-wrapper edit-mode ${this.makeClassString()}`}>
                <ComboBoxHOC
                    creatable={this.props.creatable}
                    ref={this.comboBoxHOCRef}
                    classNamePrefix="react-select"
                    value={value}
                    options={this.props.options}
                    onChange={this.onChange}
                    onKeyDown={this.keyDown}
                    autoFocus
                    isMulti={this.props.multi}
                    isClearable={this.props.clearable}
                    isValidNewOption={this.props.isValidNewOption}
                />
            </div>
        );
    };

    //copied from RIEBase but modified to slightly delay call to finishEditing to give react time to
    //persist state change
    elementBlur = (e, force) => {
        const componentArea = ReactDOM.findDOMNode(this.refs.container);
        const clickedElement = e.relatedTarget || document.activeElement;
        if (!componentArea.contains(clickedElement) || force) {
            //async react state screwing me over and parent class's 'finishEditing' won't yet see
            //the changes in state if it executes immediately after combo blurs. adding a slight
            //delay fixes this, the only workaround I could get to work without modifying 3rd party code (Select.Creatable) to use
            //callback in its setState instead see: https://reactjs.org/docs/react-component.html#setstate

            setTimeout(() => {
                // Since this is being called asynchronously, we need to check whether the component has been
                // unmounted already.  If it's unmounted, don't try to finish or react will throw an error.
                if (this._ismounted) {
                    this.finishEditing();
                }
            }, 200);
        }
    };
}



// WEBPACK FOOTER //
// ./src/components/core/editable/RIEComboBox.js